import { Injectable, inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

import { AuthHttpService } from '@app/core/auth/auth-http-service';
import { AuthRepository } from './auth.repository';
import { VirtualClassroomService } from '@app/services/vclassroom.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';

import { environment } from '@env/environment';

export const AuthenticationGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router: Router = inject(Router);
  const authHttpService: AuthHttpService = inject(AuthHttpService);
  const vclassroomService = inject(VirtualClassroomService);
  const authRepo = inject(AuthRepository);
  const featureSettings = inject(FeatureSettingsService);

  return inject(AuthHttpService)
    .checkLogin()
    .pipe(
      switchMap((authenticated) => {
        // If the user is not authenticated, redirect to sign-in
        if (!authenticated) {
          const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${encodeURIComponent(state.url)}`;
          return of(router.parseUrl(`sign-in?${redirectURL}`));
        }

        const isOnFirstStartPage = state.url.includes('/firststart');
        const isOnSignOutPage = state.url.includes('/sign-out');
        if (!isOnFirstStartPage && !isOnSignOutPage) {
          // If authenticated, check for first start
          return authHttpService.checkFirststart().pipe(
            switchMap((firststart_done) => {
              if (!firststart_done) {
                // Redirect to first start setup if needed
                return of(router.parseUrl('/firststart'));
              }

              // User is authenticated and passed firststart
              const user = authRepo.getUser();
              // console.log('🔍 [AuthGuard] Checking user for VirtualClassroom initialization:', {
              //   hasUser: !!user,
              //   user: user,
              //   supervisor_roomkey: user?.supervisor_roomkey,
              //   allKeys: user ? Object.keys(user) : [],
              //   hasRoom: !!vclassroomService.getRoom(),
              //   isInitialized: vclassroomService.isInitialized(),
              //   feature_classroom: featureSettings.isFeatureEnabled('feature_classroom')
              // });
              
              // Only initialize VirtualClassroom if feature_classroom is enabled
              // Initialize session but DON'T auto-join room - video will start when user enters a course
              if (featureSettings.isFeatureEnabled('feature_classroom') && user && !vclassroomService.isInitialized()) {
                // Get supervisor room key
                const supervisorRoomKey = user.supervisor_roomkey;
                
                if (supervisorRoomKey) {
                  console.log('🔍 [AuthGuard] Initializing VirtualClassroom session (without auto-join) with supervisor room:', supervisorRoomKey);
                  vclassroomService.initializeSession({
                    name: user.name,
                    fid: user.person_id,
                    company_id: user.company_id,
                    supervisor_room_key: supervisorRoomKey,
                    twin_id: user.twin_id,
                  }, false); // false = don't auto-join, wait until user enters course
                } else {
                  // Initialize without supervisor room key - let the service handle it
                  console.log('🔍 [AuthGuard] Initializing VirtualClassroom session (without auto-join) without supervisor room key');
                  vclassroomService.initializeSession({
                    name: user.name,
                    fid: user.person_id,
                    company_id: user.company_id,
                    supervisor_room_key: null,
                    twin_id: user.twin_id,
                  }, false); // false = don't auto-join, wait until user enters course
                }
              } else {
                const reason = !featureSettings.isFeatureEnabled('feature_classroom') ? 'feature_classroom is disabled' : 
                               !user ? 'No user' : 'Already initialized';
                console.warn('🔍 [AuthGuard] VirtualClassroom initialization skipped:', { reason });
              }

              // Allow the access if no first start needed
              return of(true);
            }),
            catchError((error) => {
              console.error('Error during first start check:', error);
              return of(router.parseUrl('/error')); // Redirect to an error page or handle as needed
            })
          );
        } else {
          //return of(true);
          return of(true);
        }
      }),
      catchError((error) => {
        console.error('Error during login check:', error);
        return of(router.parseUrl('/error')); // Redirect to an error page or handle as needed
      })
    );
};
